import {RequiredTestIdProps, suffixTestId} from 'shared/web';

interface RentalCalendarSkeletonBarsProps extends RequiredTestIdProps {
  /** Staggers the placements so rows do not line up into columns */
  rowIndex: number;
  trackWidth: number;
}

/**
 * Placeholder bars shown while a period's events load. The vehicle rows are already known at that
 * point, so the grid keeps its shape and only the track fills with shimmer.
 */
export function RentalCalendarSkeletonBars(props: RentalCalendarSkeletonBarsProps) {
  const rowOffsetRatio = ROW_OFFSET_RATIOS.at(props.rowIndex % ROW_OFFSET_RATIOS.length) ?? 0;

  return (
    <>
      {SKELETON_BARS.map((bar) => {
        const leftRatio = bar.leftRatio + rowOffsetRatio;

        return (
          <div
            key={bar.leftRatio}
            className="absolute inset-y-2 animate-pulse rounded-small bg-palettes-neutral-40-100"
            style={{
              left: Math.round(leftRatio * props.trackWidth),
              width: Math.round(bar.widthRatio * props.trackWidth),
            }}
            data-testid={suffixTestId(`skeletonBar-${props.rowIndex}-${bar.leftRatio}`, props)}
          />
        );
      })}
    </>
  );
}

/** Fixed placements — a skeleton should suggest a loaded calendar, not animate at random */
const SKELETON_BARS = [
  {leftRatio: 0.04, widthRatio: 0.16},
  {leftRatio: 0.32, widthRatio: 0.24},
  {leftRatio: 0.68, widthRatio: 0.18},
];

/**
 * Shifting a whole row by one offset keeps the bars in order — a per-bar stagger wraps the last
 * bar around and makes it overlap the first. Cycling four offsets stops the rows lining up into
 * columns, and the rightmost bar still ends at 0.95 of the track.
 */
const ROW_OFFSET_RATIOS = [0, 0.06, 0.03, 0.09];
